/** The legal documents the backend publishes (``GET /api/legal``) and
 *  the language rule for showing them.
 *
 *  The documents exist in Swedish and English only (Swedish prevails),
 *  so a Swedish UI reads them in Swedish and every other UI language in
 *  English; the page's sv/en switch overrides that. */

import type { TFunction } from "i18next";

export const LEGAL_DOCS = ["terms", "privacy", "dpa", "subprocessors", "portability"] as const;
export type LegalDoc = (typeof LEGAL_DOCS)[number];

export const LEGAL_LANGS = ["sv", "en"] as const;
export type LegalLang = (typeof LEGAL_LANGS)[number];

export function isLegalDoc(value: string | undefined): value is LegalDoc {
  return (LEGAL_DOCS as readonly string[]).includes(value ?? "");
}

export function isLegalLang(value: string | null | undefined): value is LegalLang {
  return (LEGAL_LANGS as readonly string[]).includes(value ?? "");
}

/** The document language for a UI language: sv → sv, anything else → en. */
export function legalLangFor(uiLanguage: string | null | undefined): LegalLang {
  return uiLanguage === "sv" || uiLanguage?.startsWith("sv-") ? "sv" : "en";
}

/** A document's name in the UI language. */
export function legalDocLabel(t: TFunction, doc: LegalDoc): string {
  switch (doc) {
    case "terms":
      return t("legal.doc.terms");
    case "privacy":
      return t("legal.doc.privacy");
    case "dpa":
      return t("legal.doc.dpa");
    case "subprocessors":
      return t("legal.doc.subprocessors");
    case "portability":
      return t("legal.doc.portability");
  }
}

export function legalPath(doc: LegalDoc): string {
  return `/legal/${doc}`;
}

/** The public version history of a document. */
export function legalVersionsPath(doc: LegalDoc): string {
  return `/legal/${doc}/versions`;
}

/** ``GET /api/legal/{doc}``. */
export type LegalDocument = {
  doc: LegalDoc;
  version: number;
  /** ISO date (``YYYY-MM-DD``). */
  effective: string;
  /** When the version takes effect (absent from an older backend). */
  effective_at?: string;
  title: string;
  markdown: string;
  lang: LegalLang;
};

/** What changed between two versions (``/legal/:doc/changes``). Without
 *  ``from`` and ``to`` the backend compares the latest version with the
 *  one before it. */
export function legalChangesPath(doc: LegalDoc, from?: number | null, to?: number | null): string {
  const params = new URLSearchParams();
  if (from) params.set("from", String(from));
  if (to) params.set("to", String(to));
  const query = params.toString();
  return `/legal/${doc}/changes${query ? `?${query}` : ""}`;
}

/** Where "What changed" starts for someone who last accepted or saw
 *  ``seen`` while ``latest`` is published: the seen version when it is
 *  older, otherwise the version before the latest. ``null`` when there
 *  is nothing to compare (only version 1). */
export function changesFrom(seen: number | null | undefined, latest: number): number | null {
  if (seen && seen >= 1 && seen < latest) return seen;
  return latest > 1 ? latest - 1 : null;
}

/** One side of a comparison (``from`` / ``to``). */
export type LegalChangesStamp = {
  version: number;
  published_at: string;
  effective_at: string;
  /** The effective date in Sweden, ``YYYY-MM-DD``. */
  effective: string;
};

/** A word-diff segment of a changed clause: rendered text, with the
 *  inline formatting the markdown had (bold, italics, code, a link). */
export type LegalDiffSegment = {
  op: "same" | "added" | "removed";
  text: string;
  marks?: ("strong" | "em" | "code")[];
  href?: string;
};

export type LegalChangedClause = {
  status: "added" | "removed" | "changed";
  /** A heading, a numbered clause, a table row or text before the first heading. */
  kind?: "heading" | "clause" | "row" | "columns" | "text";
  number: string | null;
  label: string;
  section: string;
  /** The clause's number in the older version when it was renumbered. */
  previous_number: string | null;
  segments: LegalDiffSegment[];
};

/** ``GET /api/legal/{doc}/changes``. */
export type LegalChanges = {
  doc: LegalDoc;
  lang: LegalLang;
  title: string;
  from: LegalChangesStamp;
  to: LegalChangesStamp;
  latest: number;
  versions: (LegalChangesStamp & {
    change_class: string;
    revision: number;
    /** Plain-language bullets of the template revision, if any. */
    summary: string[];
    /** Other changes in that version, as sentences (providers). */
    items: string[];
  })[];
  clauses: LegalChangedClause[];
  unchanged: number;
  renumbered: number;
};
